import React, { FC } from 'react';
import { Button } from '../Button';
import styled, { css } from 'styled-components';
import { TrevorIcon } from '../TrevorIcon';
import { Tooltip } from '../Tooltip';
import { nanoid } from 'nanoid';
import { useTimeoutToggle } from '../../../../src/hooks';

const CopyTextContainer = styled.div<{ width: string }>`
  display: flex;

  > input {
    background: var(--color-interface-layout-lighter);
    border-radius: var(--radius-8) 0 0 var(--radius-8);
    padding: var(--spacing-s);
    text-overflow: ellipsis;
    margin: unset;
    border: 1px solid var(--color-interface-layout-normal);
    font-weight: 500;
    font-size: 14px;
    line-height: 16px;
    color: var(--color-interface-content-normal);
    pointer-events: none;
    width: ${(props) => props.width};
  }

  > button {
    margin: unset;
    border-left: none;
    border-radius: unset;
  }

  > button:last-of-type {
    border-top-right-radius: var(--radius-100);
    border-bottom-right-radius: var(--radius-100);
  }

  ${(p) =>
    p.disabled &&
    css`
      opacity: 0.5;

      > * {
        pointer-events: none;
      }
    `}
`;

type CopyTextProps = {
  disabled?: boolean;
  disabledTooltip?: string;
  text: string;
  isUrl?: boolean;
  width?: string;
  onCopy?: F0;
  onOpen?: F0;
};

export const TextCopy: FC<CopyTextProps> = ({
  disabled,
  disabledTooltip,
  text,
  isUrl,
  width = '100%',
  onCopy,
  onOpen,
}) => {
  const [copied, toggleCopied] = useTimeoutToggle(2000);
  const tooltipCopyId = `textCopyButton-${nanoid()}`;
  const tooltipDisabledId = `disabledTextCopy-${nanoid()}`;

  const handleCopy = () => {
    window.navigator.clipboard
      .writeText(text)
      .then(() => {
        toggleCopied();
        onCopy?.();
      })
      .catch(() => window.alert('Oops, unable to copy'));
  };

  const handleOpen = () => {
    window.open(text, '_blank');
    onOpen?.();
  };

  return (
    <>
      <CopyTextContainer
        width={width}
        disabled={disabled}
        data-tooltip-id={tooltipDisabledId}
      >
        <input value={text} />

        <Button
          variant="secondary"
          onClick={handleCopy}
          data-tooltip-id={tooltipCopyId}
          icon={isUrl ? <TrevorIcon name={'copy'} /> : undefined}
        >
          {isUrl ? null : 'Copy'}
        </Button>
        <Tooltip
          id={tooltipCopyId}
          variant="success"
          place={isUrl ? 'top' : 'right'}
          isOpen={copied}
        >
          Copied!
        </Tooltip>

        {isUrl && (
          <Button
            variant="secondary"
            icon={<TrevorIcon name="export" />}
            onClick={handleOpen}
          ></Button>
        )}
      </CopyTextContainer>
      {disabled && disabledTooltip && (
        <Tooltip isMultiline id={tooltipDisabledId} float>
          {disabledTooltip}
        </Tooltip>
      )}
    </>
  );
};
